import Raven from 'raven-js'
import { Observable } from 'rxjs/Observable'
import 'rxjs/add/observable/forkJoin'
import { push } from 'connected-react-router'
import { combineEpics } from 'redux-observable'
import { setAccessToken } from '@driftt/web-auth'
import { filter } from 'lodash'
import isMobile from 'ismobilejs'
import Cookies from 'js-cookie'
import queryString from 'query-string'
import parser from 'ua-parser-js'

import config from 'config'
import * as actions from './actions'
import * as selectors from './selectors'
import * as UserActions from 'modules/user/actions'
import * as UserSelectors from 'modules/user/selectors'
import * as RoutingSelectors from 'modules/routing/selectors'
import * as BillingSelectors from 'modules/billing/selectors'
import * as MetricsActions from 'modules/metrics/actions'
import * as recaptcha from 'utils/recaptcha'
import { countAppMetric } from 'utils/metrics'
import { identifyUser, track } from 'utils/trackingUtils'
import { api } from './api'
import { api as UserApi } from 'modules/user/api'
import { Flow } from 'constants/flowConstants'
import { getVideoSignupReferrer, getVideoWebstoreSignupReferrer, sendProductEnabled, bizibleTrackNewPQL } from 'utils/trackingUtils'

const HOME_REDIRECT_URL = `${config.CUSTOMER_WEB_URL}/home`
const MARKETO_MUNCHKIN_COOKIE = '_mkto_trk'

const createOrg = (action$, store) => {
  return action$
    .ofType(actions.createOrg.toString())
    .pluck('payload')
    .mergeMap(({ email, name, password, anonymousId, configurationOptions, enableDefaultWelcomeMessages, useNetsuite, planName = '' }) => {
      const isPaidChatUser = UserSelectors.getFlow(store.getState()) === Flow.DEAL
      const captchaToken = recaptcha.getToken()
      const widgetReleaseChannel = "BETA"

      return api
          .create({
            name: name || email,
            email,
            password,
            configuration: {
              organizationName: email,
              chatEnabled: false,
              widgetMode: isPaidChatUser ? "AUTO" : "MANUAL",
              theme: {
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              }
            },
            gates: {
              chat: true
            },
            properties: {
              originalReferrer: document.referrer,
              anonymousId,
              enrollInWidgetReleaseChannel: widgetReleaseChannel,
            },
          },
          enableDefaultWelcomeMessages,
          useNetsuite,
          captchaToken
        )
        .map(response => {
          const token = response.accessToken.accessToken
          recaptcha.remove()
          setAccessToken({ token: token })
          identifyUser(undefined, response.organization.id, response.user.id)
          return actions.createOrgSuccess({ response })
        })
        .startWith(actions.createOrgPending())
        .catch((error) => {
          return Observable.of(actions.createOrgFailed(error), actions.setCreateOrgError({error}))
        })
    })
}

const countCreateOrgFailure = (action$, store) => {
  return action$
    .ofType(actions.createOrgFailed.toString())
    .pluck('payload')
    .filter(() => UserSelectors.getFlow(store.getState()) === Flow.VIDEO)
    .map((error) => {
      Raven.captureException(error, { logger: 'tracking-signup' })

      return MetricsActions.sendDriftAppMetric(countAppMetric('createOrg.failed'))
    })
}

const updateOrg = (action$, store) => {
  return action$
    .ofType(actions.updateOrg.toString())
    .pluck('payload')
    .mergeMap((configuration) => {
      const orgId = selectors.getOrgId(store.getState())
      if (!orgId) {
        return Observable.of(actions.updateOrgFailed())
      }

      return api.updateOrg(orgId, configuration)
        .map((res) => {
          return actions.updateOrgSuccess(res)
        })
        .startWith(actions.updateOrgPending())
        .catch(() => Observable.of(actions.updateOrgFailed()))
    })
}

const signup = (action$, store) => {
  return action$
    .ofType(actions.signup.toString())
    .pluck('payload')
    .delay(1000)
    .mergeMap(({ email, route }) => {

      const ua = parser()
      const flow = UserSelectors.getFlow(store.getState())
      let signupReferrer = route ? 'https://www.drift.com' + route : document.referrer
      const isThirdPartySignup = /google|microsoft/.test(route)

      if (flow === Flow.VIDEO) {
        signupReferrer = getVideoWebstoreSignupReferrer() || getVideoSignupReferrer()
      }

      window.ga && window.ga('send', 'event', 'PQL', 'PQL Email Captured')
      return api
        .trackSignup({
          email: email,
          route: signupReferrer,
          marketoMunchkinCookie: Cookies.get(MARKETO_MUNCHKIN_COOKIE),
          anonymousId: (window.analytics && window.analytics.user) ? window.analytics.user().anonymousId() : undefined,
          product: flow,
          browser: ua.browser.name,
          os: ua.os.name,
        })
        .map(actions.signupSuccess)
        .startWith(actions.signupPending({ isThirdPartySignup, email }))
        .catch(() => Observable.of(actions.signupFailed({ isThirdPartySignup })))
    })
}

const countSignupPending = (action$, store) => {
  return action$
    .ofType(actions.signupPending.toString())
    .pluck('payload')
    .filter(({ isThirdPartySignup }) => isThirdPartySignup)
    .map(({ email }) => MetricsActions.sendDriftAppMetric(
      countAppMetric(`signup.thirdParty.pending${!email ? '.noEmail' : ''}`)))
}

const countSignupFailure = (action$, store) => {
  return action$
    .ofType(actions.signupFailed.toString())
    .pluck('payload')
    .filter(({ isThirdPartySignup }) => isThirdPartySignup)
    .map(() => MetricsActions.sendDriftAppMetric(countAppMetric('signup.thirdParty.failed')))
}

const trackProductEnabledForUser = (action$, store) => {
  return action$
    .ofType(
      UserActions.fetchUserSuccess.toString()
    )
    .filter(() => !!UserSelectors.getFlow(store.getState()))
    .do(() => {
      const flow = UserSelectors.getFlow(store.getState())

      // WARNING: DO NOT CHANGE WITHOUT CONSULTING OPS AND/OR #analytics
      sendProductEnabled({ flow, triggeredBy: 'fetchUser' })
    })
    .ignoreElements()
}

const trackProductEnabledForOrg = (action$, store) => {
  return action$
    .ofType(
      actions.createOrgSuccess.toString()
    )
    .filter(() => !!UserSelectors.getFlow(store.getState()))
    .do(() => {
      const flow = UserSelectors.getFlow(store.getState())

      // WARNING: DO NOT CHANGE WITHOUT CONSULTING OPS AND/OR #analytics
      sendProductEnabled({ flow, triggeredBy: 'createOrg' })
    })
    .ignoreElements()
}

const trackOrgCreateSignup = (action$, store) => {
  return action$
    .ofType(actions.createOrgSuccess.toString())
    .pluck('payload')
    .filter(() => !UserSelectors.getDisabledEmail(store.getState()))
    .map(({ response }) => actions.signup({ email: response.user.email, route: undefined }))
}

const widgetOnboardingRedirect = (action$, store) => {
  return action$
    .ofType(
      actions.createOrgSuccess.toString(),
      actions.thirdPartyOnboardingRedirect.toString(),
    )
    .pluck('payload')
    .filter(() => [Flow.CHAT, Flow.DEAL].includes(UserSelectors.getFlow(store.getState())))
    .map(({ response }) => {
      const state = store.getState()
      const plan = BillingSelectors.getPlan(state)
      const coupon = BillingSelectors.getCoupon(state)
      const deal = BillingSelectors.getDeal(state)
      const { useNetSuite } = deal

      if (plan) {
        const queryParams = { plan: plan, coupon: coupon }
        return push(`/billing?${queryString.stringify(queryParams)}`)
      } else {
        if (useNetSuite) {
          return actions.skipRedirectAfterNetsuiteOrgCreation()
        }

        track('Onboarding Account Setup Completed', {
          context: 'widget-onboarding',
          mobile: isMobile.any,
        })
        if (isMobile.any) {
          return push('/setup/touchless/mobile')
        } else {
          return push('/setup/touchless/welcome')
        }
      }
    })
}

const videoOnboardingRedirect = (action$, store) => {
  return action$
    .ofType(
      actions.createOrgSuccess.toString(),
      actions.thirdPartyOnboardingRedirect.toString(),
    )
    .pluck('payload')
    .filter(() => UserSelectors.getFlow(store.getState()) === Flow.VIDEO)
    .mergeMap(() => {
      return Observable.create(observer => {
        // applies to mobile, desktop webstore, & normal desktop flows
        observer.next(push('/video/survey'))
      })
    })
}

const updateOrgProperties = (action$, store) => {
  return action$
    .ofType(actions.updateOrgProperties.toString())
    .pluck('payload')
    .mergeMap(({ properties, redirect, redirectUrl }) => {
      const orgId = selectors.getOrgId(store.getState())
      return api
        .updateProperties(orgId, properties)
        .map(properties => {
          if (redirect) {
            const redirectTo = redirectUrl || HOME_REDIRECT_URL
            window.location.replace(redirectTo)
          }
          return actions.updateOrgPropertiesSuccess(properties)
        })
        .startWith(actions.updateOrgPropertiesPending())
        .catch(() => Observable.of(actions.updateOrgPropertiesFailed()))
    })
}

const fetchEmbed = action$ => {
  return action$
    .ofType(actions.fetchEmbed.toString())
    .pluck('payload')
    .mergeMap(operations => {
      return api
        .rxFetchEmbed(operations)
        .map(actions.fetchEmbedSuccess)
        .startWith(actions.fetchEmbedPending())
        .catch(() => Observable.of(actions.fetchEmbedFailed()))
  })
}

const updateEmbed = action$ => {
  return action$
    .ofType(actions.updateEmbed.toString())
    .pluck('payload')
    .mergeMap(operations => {
      return api
        .updateEmbed(operations)
        .map(actions.updateEmbedSuccess)
        .startWith(actions.updateEmbedPending())
        .catch(() => Observable.of(actions.updateEmbedFailed()))
    })
}

const updateWelcomeMessage = action$ => {
  return action$
    .ofType(actions.updateWelcomeMessage.toString())
    .pluck('payload')
    .mergeMap(({ onlineText, offlineText }) => {
      return api
        .updateEmbed([{
          operation: 'set',
          value: onlineText,
          property: 'theme.welcomeMessage'
        }])
        .map(() => actions.updateWelcomeMessageSuccess({ onlineText, offlineText }))
        .startWith(actions.updateWelcomeMessagePending())
        .catch(() => Observable.of(actions.updateWelcomeMessageFailed()))
    })
}

const checkUniqueHandle = action$ => {
  return action$
    .ofType(actions.checkUniqueHandle.toString())
    .debounceTime(200)
    .pluck('payload')
    .mergeMap(handle => {
      return api
        .checkUniqueHandle(handle)
        .map(actions.checkUniqueHandleFailed)
        .catch(error => {
          if (error.status === 404) {
            return Observable.of(actions.checkUniqueHandleSuccess())
          }
          return Observable.of(actions.checkUniqueHandleFailed())
        })
    })
}

const fetchOrg = action$ => {
  return action$
    .ofType(UserActions.fetchUserSuccess.toString())
    .pluck('payload')
    .mergeMap(res => {
      return api
        .fetch(res.orgId)
        .map(actions.fetchOrgSuccess)
        .startWith(actions.fetchOrgPending())
        .catch(() => Observable.of(actions.fetchOrgFailed()))
    })
}

const fetchOrgProperties = (action$, store) => {
  return action$
    .ofType(actions.fetchOrgSuccess.toString())
    .pluck('payload')
    .mergeMap(() => {
      const orgId = selectors.getOrgId(store.getState())
      return Observable.fromPromise(api
        .fetchOrgProperties(orgId))
        .map(actions.fetchOrgPropertiesSuccess)
        .catch(() => Observable.of(actions.fetchOrgPropertiesFailed()))
    })
}

const fetchUsers = action$ => {
  return action$
    .ofType(UserActions.fetchUserSuccess.toString())
    .pluck('payload')
    .mergeMap(res => {
      const userId = res.id
      return api
        .fetchUsers(res.orgId)
        .map((res) => {
          const usersWithoutCurrentUser = filter(res, (user) => user.id !== userId)
          return actions.fetchOrgUsersSuccess(usersWithoutCurrentUser)
        })
        .startWith(actions.fetchOrgUsersPending())
        .catch(() => Observable.of(actions.fetchOrgUsersFailed()))
    })
}

const exchangeGoogleCodeForDriftCode = (action$, store) => {
  return action$
    .ofType(actions.exchangeGoogleCodeForDriftCode.toString())
    .pluck('payload')
    .mergeMap(res => {
      return api
        .exchangeGoogleCodeForDriftCode(res)
        .map(response => actions.exchangeThirdPartyCodeSuccess(response))
        .catch((error) =>  {
          return Observable.of(actions.exchangeThirdPartyCodeFailed(error), actions.setOAuthError("Sorry, we ran into an error! Try again"))
        })
    })
}

const exchangeMicrosoftCodeForDriftCode = (action$, store) => {
  return action$
    .ofType(actions.exchangeMicrosoftCodeForDriftCode.toString())
    .pluck('payload')
    .mergeMap(res => {
      return api
        .exchangeMicrosoftCodeForDriftCode(res)
        .map(response => actions.exchangeThirdPartyCodeSuccess(response))
        .catch((error) => {
          return Observable.of(actions.exchangeThirdPartyCodeFailed(error), actions.setOAuthError("Sorry, we ran into an error! Try again"))
        })
    })
}

const countThirdPartyCodeExchangeFailure = (action$, store) => {
  return action$
    .ofType(actions.exchangeThirdPartyCodeFailed.toString())
    .pluck('payload')
    .filter(() => UserSelectors.getFlow(store.getState()) === Flow.VIDEO)
    .map((error) => {
      if (!!error) {
        Raven.captureException(error, { logger: 'tracking-signup' })
        return MetricsActions.sendDriftAppMetric(countAppMetric('thirdPartyCodeExchange.failed'))
      } else {
        return MetricsActions.sendDriftAppMetric(countAppMetric('thirdPartyCodeExchange.emptyResponse'))
      }
    })
}

const exchangeThirdPartyCodeSuccess = (action$, store) => {
  return action$
    .ofType(actions.exchangeThirdPartyCodeSuccess.toString())
    .pluck('payload')
    .mergeMap((response) => {
      const state = store.getState()
      const pathname = RoutingSelectors.getPathname(state)
      let route = ""
      if (pathname.includes("google")) {
        route = pathname.replace("/start/google", "")
      } else if (pathname.includes("microsoft")) {
        route = pathname.replace("/start/microsoft", "")
      }
      if (response.message === 'Third-Party Sign Up') {
        // response contains access token
        setAccessToken({ token: response.value })
        return Observable
          .forkJoin(UserApi.fetchUser())
          .mergeMap(( value ) => {
            const email = value && value.length !== 0 && value[0].email

            return Observable.of(
              actions.trackBizibleNewPQL({ email }),
              actions.signup({ email, route }),
              actions.thirdPartyOnboardingRedirect(response))
          })
          .startWith(MetricsActions.sendDriftAppMetric(countAppMetric('signup.thirdParty.entering')))
      } else if (response.message === 'Third-Party Login') {
        // response contains auth code
        return Observable.of(actions.thirdPartyOnboardingSignIn(response))
      }
      return Observable.of(actions.exchangeThirdPartyCodeFailed())
    })
}

const thirdPartyChatSignIn = (action$, store) => {
  return action$
    .ofType(actions.thirdPartyOnboardingSignIn.toString())
    .pluck('payload')
    .filter(() => UserSelectors.getFlow(store.getState()) === Flow.CHAT)
    .map((response) => {
      const token = response.value
      const nextUrl = `${config.CUSTOMER_WEB_URL}?code=${token}`
      return window.location.replace(nextUrl)
    })
}

const thirdPartyVideoSignIn = (action$, store) => {
  return action$
    .ofType(actions.thirdPartyOnboardingSignIn.toString())
    .pluck('payload')
    .filter(() => UserSelectors.getFlow(store.getState()) === Flow.VIDEO)
    .map(({ value }) => (
      // will use webauth helper to convert code to access token & route accordingly
      push(`/video/landing/oauth?code=${value}`)
    ))
  }

const trackBizibleNewPQL = (action$, store) => {
  return action$
    .ofType(actions.trackBizibleNewPQL.toString())
    .pluck('payload')
    .do(({ email }) => {
      bizibleTrackNewPQL({ email })
    })
  }

export const epic = combineEpics(
  createOrg,
  fetchOrg,
  fetchOrgProperties,
  widgetOnboardingRedirect,
  videoOnboardingRedirect,
  updateOrgProperties,
  trackOrgCreateSignup,
  fetchEmbed,
  updateEmbed,
  updateWelcomeMessage,
  checkUniqueHandle,
  fetchUsers,
  updateOrg,
  exchangeGoogleCodeForDriftCode,
  exchangeMicrosoftCodeForDriftCode,
  exchangeThirdPartyCodeSuccess,
  thirdPartyChatSignIn,
  thirdPartyVideoSignIn,
  signup,
  trackProductEnabledForOrg,
  trackProductEnabledForUser,
  countCreateOrgFailure,
  countThirdPartyCodeExchangeFailure,
  countSignupFailure,
  countSignupPending,
  trackBizibleNewPQL,
)
